/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in child component tree and displays a fallback UI
 */
import React from 'react';
import { Box, Typography, Button, Card, CardContent } from '@mui/material';
import { Warning as WarningIcon, Refresh as RefreshIcon } from '@mui/icons-material';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console
    console.error('ErrorBoundary caught an error:', error);
    console.error('Error info:', errorInfo);

    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Report to the backend using ITS schema: {logs: [{timestamp, level,
    // message, context}]}. The previous flat payload was a guaranteed 422.
    try {
      fetch('/api/v1/logs/frontend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          logs: [{
            timestamp: new Date().toISOString(),
            level: 'error',
            message: String(error).slice(0, 1000),
            context: {
              componentStack: (errorInfo.componentStack || '').slice(0, 4000),
              url: window.location.href,
            },
          }],
        })
      }).catch(() => {
        // Silently fail if logging fails
      });
    } catch (e) {
      // Ignore logging errors
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    // Try to navigate back to dashboard
    window.location.href = '/dashboard';
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            bgcolor: '#F4EBD9',
            p: 3
          }}
        >
          <Card sx={{ maxWidth: 500, width: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <WarningIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom fontWeight="600">
                Algo salio mal
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Ha ocurrido un error inesperado. Por favor intenta recargar la pagina o volver al dashboard.
              </Typography>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <Box
                  sx={{
                    bgcolor: '#fef2f2',
                    p: 2,
                    borderRadius: 1,
                    mb: 3,
                    textAlign: 'left',
                    overflow: 'auto',
                    maxHeight: 200
                  }}
                >
                  <Typography variant="caption" component="pre" sx={{ color: '#dc2626', whiteSpace: 'pre-wrap' }}>
                    {this.state.error.toString()}
                    {this.state.errorInfo && this.state.errorInfo.componentStack}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleReload}
                >
                  Recargar Pagina
                </Button>
                <Button
                  variant="outlined"
                  onClick={this.handleReset}
                >
                  Ir al Dashboard
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
